/**
 * useAnalytics Hook
 * 
 * Provides session and room tracking for the Unity game.
 * Handles session lifecycle, room time tracking, and event emission.
 */

import { useCallback, useEffect, useRef } from 'react';
import { analyticsService } from '../services/analyticsService';

// Storage keys
const SESSION_ID_KEY = 'analytics.sessionId';
const FIRST_VISIT_KEY = 'analytics.firstVisit';
const SESSION_START_KEY = 'analytics.sessionStart';

// Heartbeat interval (30 seconds) - needed for concurrent user tracking
const HEARTBEAT_INTERVAL_MS = 30000;

// Generate UUID v4
function generateUUID(): string {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Get or create session ID (persists across page refreshes within same session)
function getSessionId(): string {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = generateUUID();
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
}

// Check if this is a returning user
function isReturningUser(): boolean {
  const firstVisit = localStorage.getItem(FIRST_VISIT_KEY);
  if (!firstVisit) {
    localStorage.setItem(FIRST_VISIT_KEY, new Date().toISOString());
    return false;
  }
  return true;
}

// Get session start time
function getSessionStartTime(): number {
  const stored = sessionStorage.getItem(SESSION_START_KEY);
  if (stored) {
    return parseInt(stored, 10);
  }
  const now = Date.now();
  sessionStorage.setItem(SESSION_START_KEY, now.toString());
  return now;
}

interface RoomState {
  roomId: string;
  roomName: string;
  enteredAt: number;
}

interface UseAnalyticsOptions {
  accessToken: string | null;
  eventId?: string;
  platform?: string;
  buildVersion?: string;
}

interface UseAnalyticsReturn {
  /** Call when Unity world is ready and session should start */
  startSession: () => void;
  /** Call when session ends (component unmount handles this automatically) */
  endSession: () => void;
  /** Call when user enters a new room */
  enterRoom: (roomId: string, roomName: string) => void;
  /** Call when user exits current room (called automatically on room change) */
  exitRoom: () => void;
  /** Track a custom event */
  trackEvent: (eventType: string, properties?: Record<string, unknown>) => void;
  /** Get current session ID */
  sessionId: string;
}

export function useAnalytics(options: UseAnalyticsOptions): UseAnalyticsReturn {
  const { accessToken, eventId = '22222', platform = 'webgpu', buildVersion = '1.0.0' } = options;
  
  const sessionIdRef = useRef<string>(getSessionId());
  const sessionStartTimeRef = useRef<number>(getSessionStartTime());
  const isSessionStartedRef = useRef<boolean>(false);
  const currentRoomRef = useRef<RoomState | null>(null);
  const isInitializedRef = useRef<boolean>(false);
  const heartbeatIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Initialize analytics service when we have an access token
  useEffect(() => {
    if (accessToken && !isInitializedRef.current) {
      analyticsService.init(accessToken);
      isInitializedRef.current = true;
    } else if (accessToken && isInitializedRef.current) {
      // Update token if it changes
      analyticsService.setAccessToken(accessToken);
    }
  }, [accessToken]);

  // Track a custom event
  const trackEvent = useCallback((eventType: string, properties: Record<string, unknown> = {}) => {
    if (!accessToken) return;
    
    analyticsService.track(eventType, {
      session_id: sessionIdRef.current,
      event_id: eventId,
      ...properties
    });
  }, [accessToken, eventId]);

  // Exit current room and track duration
  const exitRoom = useCallback(() => {
    const room = currentRoomRef.current;
    if (!room) return;

    const durationSeconds = Math.round((Date.now() - room.enteredAt) / 1000);
    
    trackEvent('room_exited', {
      room_id: room.roomId,
      room_name: room.roomName,
      duration_seconds: durationSeconds
    });

    currentRoomRef.current = null;
  }, [trackEvent]);

  // Enter a new room
  const enterRoom = useCallback((roomId: string, roomName: string) => {
    // Exit previous room first if any
    if (currentRoomRef.current && currentRoomRef.current.roomId !== roomId) {
      exitRoom();
    }

    // Don't re-enter the same room
    if (currentRoomRef.current?.roomId === roomId) {
      return;
    }

    currentRoomRef.current = {
      roomId,
      roomName,
      enteredAt: Date.now()
    };

    trackEvent('room_entered', {
      room_id: roomId,
      room_name: roomName
    });
  }, [exitRoom, trackEvent]);

  // Send heartbeat event
  const sendHeartbeat = useCallback(() => {
    if (!isSessionStartedRef.current || !accessToken) return;
    
    const room = currentRoomRef.current;
    trackEvent('heartbeat', {
      room_id: room?.roomId || null,
      room_name: room?.roomName || null
    });
  }, [accessToken, trackEvent]);

  // Start session
  const startSession = useCallback(() => {
    if (isSessionStartedRef.current || !accessToken) return;
    
    isSessionStartedRef.current = true;
    sessionStartTimeRef.current = getSessionStartTime();

    trackEvent('session_started', {
      platform,
      build_version: buildVersion,
      is_returning_user: isReturningUser()
    });

    // Start heartbeat interval for concurrent user tracking
    if (!heartbeatIntervalRef.current) {
      // Send first heartbeat immediately
      sendHeartbeat();
      heartbeatIntervalRef.current = setInterval(() => {
        sendHeartbeat();
      }, HEARTBEAT_INTERVAL_MS);
    }
  }, [accessToken, platform, buildVersion, trackEvent, sendHeartbeat]);

  // End session
  const endSession = useCallback(() => {
    if (!isSessionStartedRef.current) return;

    // Stop heartbeat
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    // Exit current room first
    exitRoom();

    const durationSeconds = Math.round((Date.now() - sessionStartTimeRef.current) / 1000);

    trackEvent('session_ended', {
      duration_seconds: durationSeconds
    });

    // Flush events immediately
    analyticsService.flush();
    
    isSessionStartedRef.current = false;
  }, [exitRoom, trackEvent]);

  // Helper to end session and flush (used by both unmount and beforeunload)
  const endSessionAndFlush = useCallback(() => {
    if (!isSessionStartedRef.current) return;

    // Stop heartbeat
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }

    // Exit room and end session
    const room = currentRoomRef.current;
    if (room) {
      const roomDuration = Math.round((Date.now() - room.enteredAt) / 1000);
      analyticsService.track('room_exited', {
        session_id: sessionIdRef.current,
        event_id: eventId,
        room_id: room.roomId,
        room_name: room.roomName,
        duration_seconds: roomDuration
      });
      currentRoomRef.current = null;
    }

    const sessionDuration = Math.round((Date.now() - sessionStartTimeRef.current) / 1000);
    analyticsService.track('session_ended', {
      session_id: sessionIdRef.current,
      event_id: eventId,
      duration_seconds: sessionDuration
    });

    // Use sync flush for reliable delivery
    analyticsService.flushSync();
    
    isSessionStartedRef.current = false;
  }, [eventId]);

  // Setup beforeunload handler for reliable session end tracking
  useEffect(() => {
    const handleBeforeUnload = () => {
      endSessionAndFlush();
    };

    const handleVisibilityChange = () => {
      // When page becomes hidden (tab close, navigate away), end session
      if (document.visibilityState === 'hidden') {
        endSessionAndFlush();
      }
    };

    // Add event listeners
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('pagehide', handleBeforeUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('pagehide', handleBeforeUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      // Also end session on unmount
      endSessionAndFlush();
    };
  }, [endSessionAndFlush]);

  return {
    startSession,
    endSession,
    enterRoom,
    exitRoom,
    trackEvent,
    sessionId: sessionIdRef.current
  };
}

export default useAnalytics;

